import React, { useState } from 'react'
import {
  browseOneDrive,
  getIndexedDocuments,
  indexDocument,
  deleteDocument,
  OneDriveItem,
  Document,
} from '../lib/api'
import { useApi } from '../hooks/useApi'
import ContextChat from '../components/ContextChat'
import OverflowMenu from '../components/OverflowMenu'

type ViewMode = 'browse' | 'indexed'

interface BreadcrumbPath {
  label: string
  path: string
}

export default function DocumentsPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('browse')
  const [browsePath, setBrowsePath] = useState('/')
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbPath[]>([{ label: 'Root', path: '/' }])
  const [selectedItem, setSelectedItem] = useState<{ id?: number; path?: string; label: string; type: string } | null>(null)

  const { data: browseData, loading: browseLoading, error: browseError } = useApi<OneDriveItem[]>(
    () => browseOneDrive(browsePath),
    [browsePath, viewMode === 'browse' ? browsePath : null]
  )

  const { data: indexedData, loading: indexedLoading, refresh: refreshIndexed } = useApi<Document[]>(
    getIndexedDocuments,
    [viewMode === 'indexed' ? 'trigger' : null]
  )

  function navigateTo(item: OneDriveItem) {
    if (item.type !== 'folder') return
    const newPath = item.path
    setBrowsePath(newPath)
    setBreadcrumbs(prev => {
      // If path already in breadcrumbs, truncate to that point
      const existing = prev.findIndex(b => b.path === newPath)
      if (existing >= 0) return prev.slice(0, existing + 1)
      return [...prev, { label: item.name, path: newPath }]
    })
    setSelectedItem(null)
  }

  function navigateToBreadcrumb(crumb: BreadcrumbPath) {
    setBrowsePath(crumb.path)
    const idx = breadcrumbs.findIndex(b => b.path === crumb.path)
    setBreadcrumbs(prev => prev.slice(0, idx + 1))
    setSelectedItem(null)
  }

  function selectFile(item: OneDriveItem) {
    if (item.indexed) {
      // Already indexed — will be loaded by chat_engine as "document"
      setSelectedItem({ path: item.path, label: item.name, type: 'onedrive_file' })
    } else {
      setSelectedItem({ path: item.path, label: item.name, type: 'onedrive_file' })
    }
  }

  async function handleIndex(item: OneDriveItem) {
    try {
      await indexDocument(item.path, item.graph_id)
      refreshIndexed()
    } catch (e) {
      alert('Failed to index: ' + e)
    }
  }

  async function handleDelete(id: number) {
    try {
      await deleteDocument(id)
      refreshIndexed()
    } catch (e) {
      alert('Failed to delete: ' + e)
    }
  }

  return (
    <div style={{ paddingBottom: 120 }}>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`nav-tab ${viewMode === 'browse' ? 'active' : ''}`}
          style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6 }}
          onClick={() => setViewMode('browse')}
        >
          Browse
        </button>
        <button
          className={`nav-tab ${viewMode === 'indexed' ? 'active' : ''}`}
          style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6 }}
          onClick={() => setViewMode('indexed')}
        >
          Indexed
        </button>
      </div>

      {viewMode === 'browse' && (
        <>
          {/* Breadcrumbs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, fontSize: 13, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.path}>
                {i > 0 && <span style={{ marginRight: 4 }}>/</span>}
                <span
                  style={{ cursor: i < breadcrumbs.length - 1 ? 'pointer' : 'default', color: i < breadcrumbs.length - 1 ? 'var(--accent)' : undefined }}
                  onClick={() => i < breadcrumbs.length - 1 && navigateToBreadcrumb(crumb)}
                >
                  {crumb.label}
                </span>
              </span>
            ))}
          </div>

          {browseLoading ? (
            <div className="loading">Loading OneDrive...</div>
          ) : browseError ? (
            <div className="empty-state">
              <div className="message">Could not load OneDrive. Check D.6 backend.</div>
            </div>
          ) : !browseData || browseData.length === 0 ? (
            <div className="empty-state">
              <div className="message">Empty folder.</div>
            </div>
          ) : (
            browseData.map((item) => (
              <div
                key={item.graph_id}
                className={`document-item${selectedItem?.path === item.path ? ' selected' : ''}`}
                onClick={() => item.type === 'folder' ? navigateTo(item) : selectFile(item)}
              >
                <span className="doc-icon">{item.type === 'folder' ? '📁' : '📄'}</span>
                <div className="doc-info">
                  <div className="doc-name">{item.name}</div>
                  {item.type === 'file' && (
                    <div className="doc-meta">
                      {item.size != null && <span>{Math.round(item.size / 1024)}KB</span>}
                      {item.modified && <span> · {new Date(item.modified).toLocaleDateString()}</span>}
                      {item.indexed && <span className="doc-indexed-badge">indexed</span>}
                    </div>
                  )}
                </div>
                {item.type === 'file' && (
                  <OverflowMenu actions={[
                    { label: item.indexed ? '↻ Re-index' : '+ Index', onClick: () => handleIndex(item) },
                  ]} />
                )}
              </div>
            ))
          )}
        </>
      )}

      {viewMode === 'indexed' && (
        <>
          {indexedLoading ? (
            <div className="loading">Loading indexed documents...</div>
          ) : !indexedData || indexedData.length === 0 ? (
            <div className="empty-state">
              <div className="message">No indexed documents yet. Browse OneDrive and index a file.</div>
            </div>
          ) : (
            indexedData.map((doc) => (
              <div
                key={doc.id}
                className={`document-item${selectedItem?.id === doc.id ? ' selected' : ''}`}
                onClick={() => setSelectedItem({ id: doc.id, label: doc.name, type: 'document' })}
              >
                <span className="doc-icon">📄</span>
                <div className="doc-info">
                  <div className="doc-name">{doc.name}</div>
                  <div className="doc-meta">
                    {doc.client_name && <span>{doc.client_name}</span>}
                    {doc.indexed_by && <span> · via {doc.indexed_by}</span>}
                    {doc.index_requested_at && <span> · {new Date(doc.index_requested_at).toLocaleDateString()}</span>}
                    {doc.has_text ? <span className="doc-indexed-badge">text ready</span> : <span style={{ color: 'var(--text-muted)' }}>no text</span>}
                  </div>
                </div>
                <OverflowMenu actions={[
                  { label: '✕ Remove from index', onClick: () => handleDelete(doc.id), danger: true },
                ]} />
              </div>
            ))
          )}
        </>
      )}

      <ContextChat
        view="documents"
        selectedItem={selectedItem ? {
          type: selectedItem.type,
          id: selectedItem.id,
          onedrivePath: selectedItem.path,
          label: selectedItem.label,
        } : null}
      />
    </div>
  )
}
